import React, { useState, useEffect } from 'react';
import StreetIcon from 'assets/svg/street-info.svg';
import HoodIcon from 'assets/svg/hood.svg';
import CityIcon from 'assets/svg/city2.svg';
import EmploymentAreaIcon from 'assets/svg/employment-area.svg';
import { useLocale } from 'locale';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { StaticMap } from 'components/static-map/StaticMap';
import {
  PageInfoWrapper,
  MapWrapper,
  MapMask,
  PageInfo,
  PageTypeLabel,
  ShareButtonWrapper,
  IconFrame,
  IconFrameWrapper,
  LabelWrapper,
  FrameDot,
  H1Label,
  PageTypeLabelWrapper,
  OpenMapIconWrapper,
} from './styled';
import ShareButton from '../navigation/share';
import FrameIcon from 'assets/svg/mapMarkerSquareFrame.svg';
import AddressIcon from 'assets/svg/address.svg';
import { CompletionType } from 'utils/entities';
import { NavigateToFn, State as RouteState } from 'config/routes';
import * as actions from 'store/state/router/actions';
import { connect } from 'react-redux';
import { CleanButton } from 'ds/components/button';
import OpenMapIcon from 'assets/svg/zoom.svg';

interface DetailsSectionProps {
  zoneName: string;
  city?: string;
  type: CompletionType;
  location: [number, number];
  borough?: string;
  route?: RouteState;
  navigateTo?: NavigateToFn;
  withMapAction?: boolean;
}

const iconsMap = {
  neighbourhood: <HoodIcon />,
  city: <CityIcon />,
  address: <AddressIcon/>,
  street: <StreetIcon />,
  employmentArea: <EmploymentAreaIcon />,
};

const IconType: React.FC<{type: string}> = ({ type }) => iconsMap[type] || null;

const Icon: React.FC<{type: string, withDot?: boolean}> = ({ type, withDot = false }) => {
  return (
    <IconFrame>
      <FrameIcon />
      <IconFrameWrapper>
        <IconType type={type}/>
      </IconFrameWrapper>
      {withDot ? <FrameDot/> : null}
    </IconFrame>
  );
};

const BaseDetailsSection: React.FC<DetailsSectionProps> = ({
  zoneName,
  city = null,
  type,
  location,
  borough,
  route,
  navigateTo,
  withMapAction,
}) => {
  const { t } = useLocale();
  // static map can't be SSRed, add it in effect
  const [ staticMapVisible, setStaticMapVisible ] = useState(false);
  const screenBreakpoint = useScreenBreakpoint();
  const isDesktop = screenBreakpoint > 2;

  useEffect(() => {
    setStaticMapVisible(true);
  }, []);

  const handleMapOpen = () => {
    if (withMapAction) {
      navigateTo(route.name, { ...route.params, isMapActive: 1 }, { replace: true });
    }
  };

  return (
    <PageInfoWrapper>
      <ShowAt at={1}>
        <MapWrapper>
          {location && staticMapVisible ? (
            <StaticMap
              lng={location[0]}
              lat={location[1]}
              pinLabel={<Icon type={type} withDot={type === CompletionType.Address} />}
            />
          ) : null}
          {!isDesktop && withMapAction ? (
            <OpenMapIconWrapper onClick={handleMapOpen}>
              <CleanButton onlyIcon mode="primaryNegative">
                <OpenMapIcon width={24} height={24} />
              </CleanButton>
            </OpenMapIconWrapper>
          ) : null}
          <MapMask />
        </MapWrapper>
      </ShowAt>
      <ShowAt from={2}>
        <IconType type={type} />
      </ShowAt>
      <PageInfo>
        <ShowAt from={2}>
          <ShareButtonWrapper>
            <ShareButton tooltip={false} />
          </ShareButtonWrapper>
        </ShowAt>
        <LabelWrapper data-auto="madlan-address-block">
          <H1Label data-auto="madlan-address-page-address" weight="bold">
            {t('localPage.zoneTitle', { zoneName, city: city || null, borough: borough || null })}
          </H1Label>
          <PageTypeLabelWrapper>
            <PageTypeLabel data-auto="madlan-address-page-type" small caps weight="bold" type={type}>
              {t('localPage.label', { type })}
            </PageTypeLabel>
          </PageTypeLabelWrapper>
        </LabelWrapper>
      </PageInfo>
    </PageInfoWrapper>
  );
};

const mapDispatchToProps = {
  navigateTo: actions.navigateTo,
};

export default connect(null, mapDispatchToProps)(BaseDetailsSection);
